import { MantineProvider, useMantineTheme } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { dateInputFormat, parseAppDate } from '../utility/dateLocale';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import 'dayjs/locale/es';
import {
  AppLocale,
  LOCALE_STORAGE_KEY,
  resolveLocale,
} from '../utility/locale';

interface LocaleContextType {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

function syncDayjsLocale(locale: AppLocale) {
  dayjs.locale(locale);
}

export const LocaleProvider: React.FC<{
  children: React.ReactNode;
  userLocale?: string | null;
}> = ({ children, userLocale }) => {
  const { i18n } = useTranslation();
  const inheritedTheme = useMantineTheme();
  const [locale, setLocaleState] = useState<AppLocale>(() => resolveLocale(userLocale));

  const setLocale = useCallback((nextLocale: AppLocale) => {
    setLocaleState(nextLocale);
    localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    void i18n.changeLanguage(nextLocale);
    syncDayjsLocale(nextLocale);
  }, [i18n]);

  useEffect(() => {
    if (userLocale) {
      setLocale(resolveLocale(userLocale));
    }
  }, [userLocale, setLocale]);

  useEffect(() => {
    void i18n.changeLanguage(locale);
    syncDayjsLocale(locale);
  }, [i18n, locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={value}>
      <MantineProvider inherit theme={{components:{...inheritedTheme.components,DateInput:{...inheritedTheme.components.DateInput,defaultProps:{
        ...inheritedTheme.components.DateInput?.defaultProps,
        valueFormat:dateInputFormat(i18n.resolvedLanguage || locale),
        dateParser:(value:string)=>parseAppDate(value,i18n.resolvedLanguage || locale),
      }}}}}>
        <DatesProvider settings={{locale:i18n.resolvedLanguage || locale}}>{children}</DatesProvider>
      </MantineProvider>
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
